// services/api.js
// ----------------------------------------------------------------------------
// Surface publique des appels API regroupés par domaine.
// La config Axios + intercepteurs (auth header, silent refresh, erreurs) vit
// désormais dans `./api/client.js` — split fait 2026-05-11 pour réduire la
// taille de ce fichier (562 LOC → ~450). Le split complet par domaine est
// listé dans la mémoire dette technique pour un refacto post-launch.
import { apiClient, axios } from './api/client';
import { API_URL } from '../config/env';

// === API regroupée ===
const API = {
  // ===== AUTH =====
  auth: {
    forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
    resetPassword: (token, motDePasse) => apiClient.post(`/auth/reset-password/${token}`, { motDePasse }),
    socialLogin: (data) => apiClient.post('/auth/social-login', data),
    verifyEmail: (token) => apiClient.get(`/auth/verify-email/${token}`),
    resendVerification: () => apiClient.post('/auth/resend-verification'),
  },

  // ===== ENFANTS =====
  enfants: {
    getMesEnfants: () => apiClient.get('/enfants/mes-enfants'),
    getEnfant: (id) => apiClient.get(`/enfants/${id}`),
    creerEnfant: (data) => apiClient.post('/enfants', data),
    modifierEnfant: (id, data) => apiClient.put(`/enfants/${id}`, data),
    supprimerEnfant: (id) => apiClient.delete(`/enfants/${id}`),
    ajouterMembre: (id, data) => apiClient.post(`/enfants/${id}/membres`, data),
    supprimerMembre: (enfantId, membreId) => apiClient.delete(`/enfants/${enfantId}/membres/${membreId}`)
  },

  // ===== ACTIVITÉS =====
  activites: {
    getActivites: (enfantId, params = {}) => {
      return apiClient.get(`/activites/${enfantId}`, { params });
    },
    modifierActivite: async (activiteId, activiteData) => {
      try {
        const response = await apiClient.put(`/activites/${activiteId}`, activiteData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Erreur lors de la modification');
      }
    },
  
    // ✅ CORRIGÉ - Endpoint photos avec bonne route
    getPhotos: (enfantId, params) => {
      return apiClient.get(`/activites/${enfantId}/photos`, { params });
    },
    
    // ✅ NOUVEAU - Télécharger une photo spécifique
    downloadPhoto: async (enfantId, photoId, filename = null) => {
      try {
        const response = await axios({
          method: 'GET',
          url: `/api/activites/${enfantId}/photos/${photoId}/download`,
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`
          }
        });
        
        // Créer un lien de téléchargement
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        
        // Utiliser le filename fourni ou extraire depuis les headers
        let downloadFilename = filename;
        if (!downloadFilename) {
          const disposition = response.headers['content-disposition'];
          if (disposition) {
            const filenameMatch = disposition.match(/filename="(.+)"/);
            downloadFilename = filenameMatch ? filenameMatch[1] : `photo_${photoId}.webp`;
          } else {
            downloadFilename = `photo_${photoId}.webp`;
          }
        }
        
        link.setAttribute('download', downloadFilename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true, filename: downloadFilename };
        
      } catch (error) {
        if (error.response?.status === 403) {
          throw new Error(error.response.data?.message || 'Quota de téléchargement atteint');
        }
        if (error.response?.status === 404) {
          throw new Error('Photo non trouvée');
        }
        
        throw new Error('Erreur lors du téléchargement de la photo');
      }
    },

    creerActivite: (data) => apiClient.post('/activites', data),
    
    creerPhoto: (enfantId, formData, onUploadProgress = null) => {
      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      };
      if (onUploadProgress) {
        config.onUploadProgress = (e) => {
          const progress = Math.round((e.loaded * 100) / e.total);
          onUploadProgress(progress);
        };
      }
      return apiClient.post(`/activites/${enfantId}/photo`, formData, config);
    },
    
    supprimerActivite: (id) => apiClient.delete(`/activites/${id}`),
    supprimerPhoto: (activiteId, photoIndex) => apiClient.delete(`/activites/${activiteId}/photos/${photoIndex}`),
    getStatistiques: (enfantId, periode = '7d') => apiClient.get(`/enfants/${enfantId}/activites/stats?periode=${periode}`),
    rechercherActivites: (enfantId, query) => apiClient.get(`/enfants/${enfantId}/activites/search?q=${encodeURIComponent(query)}`)
  },

  // ===== PUBLICATIONS =====
  publications: {
    getPublications: (params = {}) => apiClient.get('/publications', { params }),
    creerPublication: (data) => apiClient.post('/publications', data),
    uploadPublicationMedia: (formData, onUploadProgress) =>
      apiClient.post('/publications/upload-media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onUploadProgress
          ? (e) => onUploadProgress(Math.round((e.loaded * 100) / (e.total || 1)))
          : undefined,
      }),
    modifierPublication: (id, formData) => apiClient.put(`/publications/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    supprimerPublication: (id) => apiClient.delete(`/publications/${id}`),
    checkUserLike: (id) => apiClient.get(`/publications/${id}/check-like`),
    toggleLike: (id) => apiClient.post(`/publications/${id}/like`),
    getLikes: (id, params = {}) => apiClient.get(`/publications/${id}/likes`, { params }),
    getCommentaires: (id, params = {}) => apiClient.get(`/publications/${id}/commentaires`, { params }),
    ajouterCommentaire: (id, data) => apiClient.post(`/publications/${id}/commentaires`, data),
    supprimerCommentaire: (pubId, commentId) => apiClient.delete(`/publications/${pubId}/commentaires/${commentId}`),
    modifierCommentaire: (pubId, commentId, data) => apiClient.put(`/publications/${pubId}/commentaires/${commentId}`, data)
  },

  freemium: {
    getUsage: () => apiClient.get('/freemium/usage'),
    getLimits: () => apiClient.get('/freemium/limits'),
    checkQuota: (action, type = null) => apiClient.post('/freemium/check-quota', { action, type })
  },

  // ===== CAPSULES / COFFRE =====
  capsules: {
    getCapsules: (enfantId) => apiClient.get(`/capsules/${enfantId}`),
    getStats: (enfantId) => apiClient.get(`/capsules/${enfantId}/stats`),
    getDetail: (id) => apiClient.get(`/capsules/detail/${id}`),
    mesCapsules: () => apiClient.get('/capsules/mes-capsules'),
    upcoming: () => apiClient.get('/capsules/upcoming'),
    creerCapsule: (enfantId, formData) =>
      apiClient.post(`/capsules/${enfantId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      }),
    scellerDepuisPublication: (enfantId, publicationId, data) =>
      apiClient.post(`/capsules/${enfantId}/from-publication/${publicationId}`, data),
    scellerDepuisMessage: (enfantId, messageId, data) =>
      apiClient.post(`/capsules/${enfantId}/from-message/${messageId}`, data),
    supprimerCapsule: (id) => apiClient.delete(`/capsules/${id}`)
  },

  // ===== SOUVENIRS =====
  souvenirs: {
    getSouvenirs: (enfantId) => apiClient.get(`/souvenirs/${enfantId}`),
    getEtapes: (enfantId) => apiClient.get(`/souvenirs/${enfantId}/etapes`),
    genererCarte: (enfantId, data) => apiClient.post(`/souvenirs/${enfantId}/carte`, data),
  },

  // ===== MESSAGES =====
  messages: {
    getRooms: () => apiClient.get('/messages/rooms'),
    getMessagesEnfant: (enfantId, params = {}) => apiClient.get(`/messages/enfant/${enfantId}`, { params }),
    envoyerMessageEnfant: (enfantId, data) => {
      return apiClient.post(`/messages/enfant/${enfantId}`, data);
    },
    getContacts: () => apiClient.get('/messages/contacts'),
    getConversation: (enfantId, autreId) => apiClient.get(`/messages/conversation/${enfantId}/${autreId}`),
    envoyerMessage: (data) => {
      return apiClient.post('/messages', data);
    }
  },

  // ===== NOTIFICATIONS =====
  notifications: {
    getNotifications: (params = {}) => {
      const queryString = new URLSearchParams({ limit: 20, page: 1, ...params }).toString();
      return apiClient.get(`/notifications?${queryString}`);
    },
    getCompteNonLues: () => apiClient.get('/notifications/unread-count'),
    marquerCommeLue: (id) => apiClient.patch(`/notifications/${id}/read`),
    marquerToutesLues: () => apiClient.patch('/notifications/mark-all-read')
  },

  // ===== JOURNAL =====
  journal: {
    getQuotidien: (enfantId, date) => apiClient.get(`/journal/quotidien/${enfantId}`, { params: { date } }),
    getHebdomadaire: (enfantId, semaine) => apiClient.get(`/journal/hebdomadaire/${enfantId}`, { params: { semaine } }),
    getMensuel: (enfantId, mois) => apiClient.get(`/journal/mensuel/${enfantId}`, { params: { mois } }),
  },

  // ===== UTILISATEURS =====
  utilisateurs: {
    getProfile: () => apiClient.get('/auth/profil'),
    updateProfil: (data) => apiClient.put('/utilisateurs/profil', data),
    rechercher: (query) => apiClient.get('/utilisateurs/recherche', { params: { q: query } }),
    ajouterTokenFCM: (token) => apiClient.post('/utilisateurs/token-fcm', { token }),
    supprimerTokenFCM: (token) => apiClient.delete('/utilisateurs/token-fcm', { data: { token } }),
    getMesPermissions: () => apiClient.get('/utilisateurs/mes-permissions'),
    verifierPermission: (enfantId, permission) => apiClient.get(`/utilisateurs/permission/${enfantId}/${permission}`),
    getFreemiumUsage: () => apiClient.get('/utilisateurs/freemium-usage'),
    getStats: () => apiClient.get('/utilisateurs/stats'),
    changePassword: (data) => apiClient.put('/utilisateurs/change-password', data),
    demanderSuppression: (data) => apiClient.post('/utilisateurs/demande-suppression', data),
    annulerSuppression: () => apiClient.post('/utilisateurs/annuler-suppression'),
    // RGPD Art.20 — déclenche un téléchargement direct du JSON via axios brut
    // (l'intercepteur de réponse renvoie response.data, on a besoin de l'objet
    // complet pour récupérer le blob + content-disposition).
    exporterDonneesRGPD: async () => {
      const response = await axios.get('/api/utilisateurs/export-rgpd', {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        withCredentials: true
      });
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const filenameMatch = disposition && disposition.match(/filename="(.+)"/);
      link.download = (filenameMatch && filenameMatch[1]) || `titaton-mes-donnees-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return { success: true, filename: link.download };
    }
  },

  // ===== PARRAINAGE =====
  parrainage: {
    getMonCode: () => apiClient.get('/parrainage/mon-code'),
    getMesParrainages: () => apiClient.get('/parrainage/mes-parrainages'),
    inviter: (email) => apiClient.post('/parrainage/inviter', { email }),
    validerCode: (code) => apiClient.get(`/parrainage/valider/${code}`),
  },

  stripe: {
    async createCheckoutSession() {
      try {
        const response = await apiClient.post('/stripe/create-checkout-session');
        
        let data = response.data;
        if (!data && response.response) {
          data = response.response.data;
        }
        if (!data && typeof response === 'object' && response.success) {
          data = response;
        }
        
        if (data && (data.url || data.sessionId)) {
          return data;
        } else {
          throw new Error('Réponse invalide du serveur');
        }
      } catch (error) {
        throw error;
      }
    },

    async createPortalSession() {
      // L'intercepteur global retourne déjà response.data, donc `response` ici
      // est l'objet { success, url } directement.
      return await apiClient.post('/stripe/create-portal-session');
    },

    async redirectToPortal() {
      const data = await apiClient.post('/stripe/create-portal-session');
      if (!data?.url) throw new Error('URL de portail manquante.');
      window.location.href = data.url;
    },

    async getSubscriptionStatus() {
      return await apiClient.get('/stripe/subscription-status');
    },

    requestRefund: (motif) => apiClient.post('/stripe/request-refund', { motif }),
  },

  // ===== QUOTAS =====
  quotas: {
    getMesQuotas: () => apiClient.get('/quotas'),
    getQuota: (type, date = null) => {
      const params = date ? `?date=${date}` : '';
      return apiClient.get(`/quotas/${type}${params}`);
    }
  },

  // ===== SIGNALEMENTS =====
  signalements: {
    creer: (data) => apiClient.post('/signalements', data),
    lister: (params) => apiClient.get('/signalements', { params }),
    traiter: (id, data) => apiClient.put(`/signalements/${id}`, data)
  },

  // ===== RECLAMATIONS =====
  reclamations: {
    creer: (data) => apiClient.post('/reclamations', data),
    mesReclamations: () => apiClient.get('/reclamations/mes'),
    lister: (params) => apiClient.get('/reclamations', { params }),
    traiter: (id, data) => apiClient.put(`/reclamations/${id}`, data)
  },

  invitations: {
    creerInvitation: (data) => apiClient.post('/invitations', data),
    listerRecues: (params = {}) => apiClient.get('/invitations/recues', { params }),
    listerEnvoyees: (params = {}) => apiClient.get('/invitations/envoyees', { params }),
    repondre: (id, data) => apiClient.post(`/invitations/${id}/repondre`, data),
    traiterToken: (token) => apiClient.get(`/invitations/token/${token}`),
    refuserParToken: (token) => apiClient.post(`/invitations/token/${token}/refuser`),
    rechercherUtilisateurs: (params) => apiClient.get('/invitations/recherche-utilisateurs', { params }),
    annuler: (id) => apiClient.delete(`/invitations/${id}`),
    renvoyer: (id) => apiClient.post(`/invitations/${id}/renvoyer`)
  },

  // ===== MEDIA / UPLOADS ===== 
  media: {
    getImageUrl(relativePath) {
      if (!relativePath) return null;
      if (relativePath.startsWith('http')) return relativePath;
      const base = API_URL;
      return `${base}${relativePath}`;
    },
    
    getThumbnailUrl(relativePath) {
      return this.getImageUrl(relativePath);
    },
    
    // ✅ AMÉLIORÉ - Méthode de téléchargement générique
    async downloadImage(url, filename) {
      try {
        const res = await fetch(url);
        const blob = await res.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename || 'photo.webp';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        return true;
      } catch (err) {
        throw new Error('Impossible de télécharger l\'image');
      }
    }
  },

  // ===== ADMIN =====
  admin: {
    getStats: () => apiClient.get('/admin/stats'),
    listerUtilisateurs: (params) => apiClient.get('/admin/utilisateurs', { params }),
    modifierUtilisateur: (id, data) => apiClient.put(`/admin/utilisateurs/${id}`, data),
    listerSignalements: (params) => apiClient.get('/admin/signalements', { params }),
    traiterSignalement: (id, data) => apiClient.put(`/admin/signalements/${id}`, data),
    getRefunds: (params) => apiClient.get('/admin/refunds', { params }),
    approveRefund: (id) => apiClient.put(`/admin/refunds/${id}/approve`),
    rejectRefund: (id, notes) => apiClient.put(`/admin/refunds/${id}/reject`, { notes }),
    // Config
    getConfigs: () => apiClient.get('/admin/config'),
    updateConfig: (cle, data) => apiClient.put(`/admin/config/${cle}`, data),
    updateConfigBatch: (configs) => apiClient.post('/admin/config/batch', { configs }),
    initConfigs: () => apiClient.post('/admin/config/init'),
    toggleBeta: () => apiClient.post('/admin/config/toggle-beta'),
    // Reclamations
    listerReclamations: (params) => apiClient.get('/admin/reclamations', { params }),
    traiterReclamation: (id, data) => apiClient.put(`/admin/reclamations/${id}`, data),
    // Stockage / Quotas
    getStorageOverview: () => apiClient.get('/admin/storage/overview'),
    getStorageAccount: (userId) => apiClient.get(`/admin/storage/account/${userId}`),
    recalculateStorage: (userId) => apiClient.post(`/admin/storage/recalculate/${userId}`),
    recalculateAllStorage: () => apiClient.post('/admin/storage/recalculate-all'),
    updateStorageLimit: (userId, storageLimitMB) => apiClient.put(`/admin/storage/limit/${userId}`, { storageLimitMB }),
    getStorageAlerts: () => apiClient.get('/admin/storage/alerts'),
  },

  // ===== PHENIX =====
  phenix: {
    lister: (params = {}) => apiClient.get('/phenix', { params }),
    getQuota: () => apiClient.get('/phenix/quota'),
    reveal: (pubId) => apiClient.post(`/phenix/reveal/${pubId}`),
    partager: (pubId) => apiClient.post(`/phenix/reveal/${pubId}/partager`),
    archive: (params = {}) => apiClient.get('/phenix/archive', { params }),
  },

  // ===== GARDIEN =====
  gardien: {
    createSession: (enfantId) => apiClient.post('/stripe/create-gardien-session', { enfantId }),
  },

  // ===== PARTAGE =====
  partage: {
    creer: (data) => apiClient.post('/partage', data),
    getPartage: (code) => apiClient.get(`/partage/${code}`),
    mesPartages: () => apiClient.get('/partage/mes-partages'),
    supprimer: (code) => apiClient.delete(`/partage/${code}`),
  },

  // ===== UTILS =====
  utils: {
    ping: () => apiClient.get('/ping'),
    getLimites: () => apiClient.get('/limits')
  }
};

// === Helpers Navigation ===
export const NavigationHelpers = {
  // ✅ NOUVEAU - Helpers pour les routes enfants
  getEnfantActivitesUrl: (enfantId) => `/enfants/${enfantId}/activites`,
  getEnfantPhotosUrl: (enfantId) => `/enfants/${enfantId}/photos`,
  getEnfantProfilUrl: (enfantId) => `/enfants/${enfantId}`,
  getEnfantMessagesUrl: (enfantId) => `/enfants/${enfantId}/messages`,
  
  // Validation d'ID
  isValidEnfantId: (id) => {
    return id && typeof id === 'string' && id.length >= 20;
  }
};

// === Helpers Activité ===
export const ActiviteHelpers = {
  formatActivite(activite) {
    return {
      ...activite,
      heureFormatee: new Date(activite.heureDebut).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      dateFormatee: new Date(activite.heureDebut).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    };
  },
  
  grouperParDate(activites) {
    return activites.reduce((acc, act) => {
      const date = new Date(act.heureDebut).toDateString();
      acc[date] = acc[date] || [];
      acc[date].push(act);
      return acc;
    }, {});
  },
  
  filtrerParType(activites, type) {
    return activites.filter(act => act.type === type);
  },
  
  getIconeType(type) {
    const map = {
      photo: '📸', repas: '🍽️', sommeil: '😴', jeu: '🎮',
      promenade: '🚶‍♀️', lecture: '📚', change: '👶',
      medicament: '💊', autre: '📝'
    };
    return map[type] || map.autre;
  },
  
  getCouleurType(type) {
    const map = {
      photo: '#f59e0b', repas: '#10b981', sommeil: '#6366f1', jeu: '#ef4444',
      promenade: '#8b5cf6', lecture: '#06b6d4', change: '#f97316',
      medicament: '#e11d48', autre: '#6b7280'
    };
    return map[type] || map.autre;
  }
};

// ✅ NOUVEAU - Helpers pour les photos
export const PhotoHelpers = {
  // Générer l'ID de photo pour le téléchargement
  generatePhotoId: (activiteId, photoIndex) => {
    return `${activiteId}_${photoIndex}`;
  },
  
  // Parser l'ID de photo
  parsePhotoId: (photoId) => {
    const parts = photoId.split('_');
    if (parts.length !== 2) return null;
    return {
      activiteId: parts[0],
      photoIndex: parseInt(parts[1])
    };
  },
  
  // Générer un nom de fichier pour téléchargement
  generateDownloadFilename: (enfantNom, date, photoIndex) => {
    const dateStr = date instanceof Date ? 
      date.toISOString().split('T')[0] : 
      new Date(date).toISOString().split('T')[0];
    return `${enfantNom}_${dateStr}_photo_${photoIndex + 1}.webp`;
  },
  
  // Vérifier si une photo est blurrée
  isPhotoBlurred: (photo) => {
    return photo.isBlurred === true;
  }
};

export default API;
